// Application Constants

import changelog from '@/data/changelog.json'

export const APP_NAME = import.meta.env.VITE_APP_NAME || 'SignWiz'
// La version se inyecta via scripts/generate-changelog.mjs en prebuild,
// que combina src/data/changelog-seed.json (curado) + git log + actualiza
// package.json. La env VITE_APP_VERSION queda como override manual.
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || (changelog as { version: string }).version || '0.1.0'
// Empty string for production = relative URLs (same origin, no CORS)
// Falls back to localhost:8000 for development
export const API_URL = import.meta.env.VITE_API_URL ??
  (import.meta.env.MODE === 'production' ? '' : 'http://localhost:8000')
export const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1'

// Authentication
export const AUTH_ENABLED = import.meta.env.VITE_AUTH_ENABLED === 'true'
export const SIMPLE_AUTH_PASSWORD = import.meta.env.VITE_SIMPLE_AUTH_PASSWORD || ''
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// File Upload
export const MAX_FILE_SIZE_MB = Number(import.meta.env.VITE_MAX_FILE_SIZE_MB) || 10
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
export const ALLOWED_FILE_TYPES = ['application/pdf']

// Signature Status Config
export const SIGNATURE_STATUS_CONFIG = {
  FIRMA_DIGITAL_VALIDA: {
    label: 'Firma Digital Válida',
    icon: '✅',
    color: 'signature-valid',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    textColor: 'text-green-800',
  },
  FIRMA_ELECTRONICA_DETECTADA: {
    label: 'Firma Electrónica Detectada',
    icon: '⚠️',
    color: 'signature-electronic',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-500',
    textColor: 'text-yellow-800',
  },
  SIN_FIRMA_VALIDA: {
    label: 'Sin Firma Válida',
    icon: '❌',
    color: 'signature-invalid',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-500',
    textColor: 'text-gray-800',
  },
  ERROR: {
    label: 'Error en Verificación',
    icon: '⛔',
    color: 'signature-error',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-500',
    textColor: 'text-red-800',
  },
} as const

// Legal Messages (Argentina - Ley 25.506)
export const LEGAL_MESSAGES = {
  FIRMA_DIGITAL: 'Tiene plena validez y presunción de autoría e integridad según Ley 25.506.',
  FIRMA_ELECTRONICA: 'Tiene menor valor probatorio. Requiere pruebas adicionales para acreditar autenticidad.',
  SIN_FIRMA: 'No posee valor probatorio de firma.',
  ERROR: 'No se pudo completar la verificación. Por favor intente nuevamente.',
} as const

// API Endpoints
export const API_ENDPOINTS = {
  VERIFY_SIGNATURE: '/api/verify-pdf',
  REGISTER_CUSTODY: '/api/custody',
  GET_CUSTODY: '/api/custody',
  HEALTH: '/api/health',
} as const

// Generate verification URL (uses current window location in browser)
export const getVerificationUrl = (verificationId: string): string => {
  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (!verificationId || !uuidRegex.test(verificationId)) {
    console.error('Invalid verification ID:', verificationId)
    return '#'
  }

  if (typeof window === 'undefined') {
    // SSR fallback
    return `https://signwiz.app/verify/${verificationId}`
  }

  // Use current origin for dev/preview/production
  return `${window.location.origin}/verify/${verificationId}`
}

// Generate custody URL (uses current window location in browser)
export const getCustodyUrl = (custodyId: string): string => {
  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (!custodyId || !uuidRegex.test(custodyId)) {
    console.error('Invalid custody ID:', custodyId)
    return '#'
  }

  if (typeof window === 'undefined') {
    // SSR fallback
    return `https://signwiz.app/d/${custodyId}`
  }

  // Use current origin for dev/preview/production
  return `${window.location.origin}/d/${custodyId}`
}
